import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { useBooking } from '../hooks/useBooking';
import { createBookingRequest, hashSHA256, checkApprovedBooker } from '../utils/firebase';
import { formatTimeForDisplay } from '../utils/availability';
import { formatPrice } from '../types';
import type { ConfirmationMode } from '../types';
import { countryCodes } from '../data/countryCodes';
import { BookingStepper } from '../components/BookingStepper';
import { executeRecaptchaV3 } from '../utils/recaptcha';

export function BookingConfirmationPage() {
  const navigate = useNavigate();
  const {
    bookingState,
    businessListing,
    resetBooking,
    isApprovedBooker,
    depositSettings,
  } = useBooking();


  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [finalStatus, setFinalStatus] = useState<'confirmed' | 'pending' | null>(null);

  const {
    selectedService,
    selectedDate,
    selectedTime,
    customerInfo,
  } = bookingState;

  // Validate state and redirect if missing
  useEffect(() => {
    if (!selectedService || !selectedDate || !selectedTime || !customerInfo.firstName || !customerInfo.lastName) {
      navigate('/book');
    }
  }, [selectedService, selectedDate, selectedTime, customerInfo, navigate]);

  if (!selectedService || !selectedDate || !selectedTime || !customerInfo.firstName || !customerInfo.lastName) {
    return null;
  }

  const confirmationMode: ConfirmationMode = businessListing?.confirmation_mode || 'instant';

  // For hashedcheck modes, determine if this will be a confirmed or pending booking
  // based on the pre-computed approval check from CustomerDetailsPage

  const willBeConfirmed = confirmationMode === 'instant' ||
    (['selectClients', 'selectGroups'].includes(confirmationMode) && isApprovedBooker === true);

  const handleConfirmBooking = async () => {
    if (!businessListing) {
      setError('Business information not found. Please try again.');
      return;
    }

    // Deposit flow — hand off to payment page
    if (depositSettings) {
      const slotStart = new Date(
        `${format(selectedDate, 'yyyy-MM-dd')}T${selectedTime}:00`
      );
      const slotEnd = new Date(slotStart.getTime() + selectedService.duration_minutes * 60_000);
      navigate('/book/payment', {
        state: {
          listingId: businessListing.id,
          serviceId: selectedService.id,
          service: selectedService,
          slotStart: slotStart.toISOString(),
          slotEnd: slotEnd.toISOString(),
          customerInfo,
          depositSettings,
        },
      });
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const mode = businessListing.confirmation_mode || 'instant';

      // ── Step 1: Hash contact details for the approved-booker check ──
      // The function decides the status; the local check only picks the message if it doesn't say.
      const emailHash = customerInfo.email?.trim()
        ? await hashSHA256(customerInfo.email.trim().toLowerCase())
        : null;

      // Hash digits only, no dial code (must match admin hash contract)
      const phoneDigits = customerInfo.phone?.replace(/\D/g, '') || '';
      const phoneHash = phoneDigits.length >= 10
        ? await hashSHA256(phoneDigits)
        : null;

      let expectedStatus: 'confirmed' | 'pending' = 'pending';
      if (mode === 'instant') {
        expectedStatus = 'confirmed';
      } else if (['selectClients', 'selectGroups'].includes(mode)) {
        const isApproved = await checkApprovedBooker(businessListing.id, emailHash, phoneHash);
        expectedStatus = isApproved ? 'confirmed' : 'pending';
      }

      const preferredDatetime = `${format(selectedDate, 'yyyy-MM-dd')}T${selectedTime}`;

      // ── Step 2: Run reCAPTCHA v3 ──
      const recaptchaToken = await executeRecaptchaV3('booking_confirm');

      // ── Step 3: Create the booking ──
      const result = await createBookingRequest({
        listingId: businessListing.id,
        serviceId: selectedService.id,
        preferredDatetime,
        serviceDuration: selectedService.duration_minutes,
        customerInfo: {
          firstName: customerInfo.firstName,
          lastName: customerInfo.lastName,
          email: customerInfo.email,
          phone: customerInfo.phone,
          countryCode: customerInfo.countryCode,
          notes: customerInfo.notes,
        },
        emailHash,
        phoneHash,
        recaptchaToken,
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to create booking request');
      }

      // Anon users can't read booking_requests, so the function returns the status it chose
      const bookingStatus = result.status ?? expectedStatus;
      setFinalStatus(bookingStatus);
      setSuccess(true);

      // Redirect to success page after a short delay
      setTimeout(() => {
        navigate('/book/success', { state: { bookingStatus } });
      }, 1500);

    } catch (err: any) {
      if (err?.message === 'SLOT_UNAVAILABLE') {
        navigate('/book/date-time', {
          state: { error: 'That time slot is no longer available. Please select another.' },
        });
        return;
      }
      if (err?.message === 'RECAPTCHA_NOT_CONFIGURED') {
        console.error('reCAPTCHA site key not configured');
        setError('Unexpected configuration error. Please try again later.');
      } else if (err?.message === 'RECAPTCHA_FAILED' || err?.message === 'RECAPTCHA_NOT_AVAILABLE') {
        console.error('reCAPTCHA execution failed:', err);
        setError('We could not verify your request. Please reload the page and try again.');
      } else {
        console.error('Error creating booking:', err);
        setError('Failed to create booking request. Please try again or contact us directly.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-site-bg text-site-text">
      {/* Header with Stepper */}
      <BookingStepper currentStep={4} />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Booking Details */}
        <div className="bg-site-bg rounded-2xl border border-site-border p-6 mb-6">
          <h2 className="text-lg font-semibold text-site-text mb-4">
            Booking Details
          </h2>

          <div className="space-y-4">
            {/* Service */}
            <div>
              <div className="text-sm text-site-muted mb-1">Service</div>
              <div className="font-medium text-site-text">{selectedService.name}</div>
              {selectedService.description && (
                <div className="text-sm text-site-muted mt-1">
                  {selectedService.description}
                </div>
              )}
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-site-muted mb-1">Date</div>
                <div className="font-medium text-site-text">
                  {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                </div>
              </div>

              <div>
                <div className="text-sm text-site-muted mb-1">Time</div>
                <div className="font-medium text-site-text">
                  {formatTimeForDisplay(selectedTime)}
                </div>
              </div>
            </div>

            {/* Duration & Price */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-site-muted mb-1">Duration</div>
                <div className="font-medium text-site-text">
                  {selectedService.duration_minutes} minutes
                </div>
              </div>

              <div>
                <div className="text-sm text-site-muted mb-1">Price</div>
                <div className="font-semibold text-site-text text-lg">
                  {formatPrice(selectedService.price_cents, selectedService.currency)}
                </div>
              </div>
            </div>

            {/* Customer Info */}
            <div className="pt-4 border-t border-site-border">
              <div className="text-sm text-site-muted mb-2">Your Information</div>
              <div className="space-y-1">
                <div className="font-medium text-site-text">
                  {customerInfo.firstName} {customerInfo.lastName}
                </div>
                <div className="text-sm text-site-muted">
                  {countryCodes.find(c => c.code === customerInfo.countryCode)?.dial_code} {customerInfo.phone}
                </div>
                {customerInfo.email && (
                  <div className="text-sm text-site-muted">{customerInfo.email}</div>
                )}
              </div>
            </div>

            {/* Notes */}
            {customerInfo.notes && (
              <div>
                <div className="text-sm text-site-muted mb-1">Special Requests</div>
                <div className="text-sm text-site-text bg-site-bg p-3 rounded-lg">
                  {customerInfo.notes}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Approval Notice - for request_to_book, or automatic mode when not pre-approved */}
        {(confirmationMode === 'request_to_book' || (['selectClients', 'selectGroups'].includes(confirmationMode) && !willBeConfirmed)) && (
          <div className="bg-site-warning-soft border border-site-warning/40 rounded-2xl p-4 mb-6">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-site-warning flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-site-warning mb-1">
                  Booking Requires Approval
                </div>
                <div className="text-sm text-site-warning">
                  Your booking request will be reviewed and you'll receive a confirmation once approved.
                  {businessListing?.public_phone && (
                    <> You can also call us at{' '}
                      <a href={`tel:${businessListing.public_phone}`} className="font-medium underline">
                        {businessListing.public_phone}
                      </a>.
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-site-danger-soft border border-site-danger/40 rounded-2xl p-4 mb-6">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-site-danger flex-shrink-0 mt-0.5" />
              <div className="text-sm text-site-danger">{error}</div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="bg-site-success-soft border border-site-success/40 rounded-2xl p-6 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-site-success rounded-full flex items-center justify-center">
                <Check className="w-6 h-6 text-site-bg" />
              </div>
              <div>
                <div className="font-semibold text-site-success text-lg">
                  {finalStatus === 'confirmed' ? 'Booking Confirmed!' : 'Booking Request Submitted!'}
                </div>
                <div className="text-sm text-site-success">
                  {finalStatus === 'confirmed'
                    ? 'Your booking is confirmed!'
                    : (
                      <>
                        Your booking request is pending approval.
                        {businessListing?.public_phone && (
                          <> Call{' '}
                            <a href={`tel:${businessListing.public_phone}`} className="font-medium underline">
                              {businessListing.public_phone}
                            </a>{' '}for questions.
                          </>
                        )}
                      </>
                    )
                  }
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Deposit Summary */}
        {depositSettings && (
          <div className="bg-site-bg rounded-2xl border border-site-border p-6 mb-6">
            <h2 className="text-lg font-semibold text-site-text mb-4">Payment</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-site-muted">Service price</span>
                <span className="font-medium text-site-text">
                  {formatPrice(selectedService.price_cents, selectedService.currency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-site-muted">
                  Deposit due today&nbsp;
                  <span className="text-site-muted">({depositSettings.percent}%)</span>
                </span>
                <span className="font-semibold" style={{ color: 'var(--site-link)' }}>
                  {formatPrice(
                    Math.round((selectedService.price_cents * depositSettings.percent) / 100),
                    selectedService.currency,
                  )}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-site-border">
                <span className="text-site-muted">Balance at appointment</span>
                <span className="font-medium text-site-text">
                  {formatPrice(
                    selectedService.price_cents - Math.round((selectedService.price_cents * depositSettings.percent) / 100),
                    selectedService.currency,
                  )}
                </span>
              </div>
            </div>
            {depositSettings.policyText && (
              <details className="mt-4">
                <summary className="text-sm font-medium cursor-pointer" style={{ color: 'var(--site-link)' }}>
                  Cancellation policy
                </summary>
                <p className="mt-2 text-sm text-site-muted leading-relaxed">
                  {depositSettings.policyText}
                </p>
              </details>
            )}
          </div>
        )}

        {/* Confirm Button */}
        {!success && (
          <button
            onClick={handleConfirmBooking}
            disabled={submitting}
            className="w-full bg-site-primary text-site-on-primary border border-site-primary-outline font-semibold py-4 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-site-on-primary" />
                <span>{depositSettings ? 'Loading...' : (willBeConfirmed ? 'Confirming Booking...' : 'Submitting Request...')}</span>
              </>
            ) : (
              <span>
                {depositSettings
                  ? `Pay deposit to confirm`
                  : (willBeConfirmed ? 'Confirm Booking' : 'Request Booking')}
              </span>
            )}
          </button>
        )}

        {/* Contact Info */}
        <div className="mt-6 text-center text-sm text-site-muted">
          Questions? Contact us at{' '}
          {businessListing?.public_email && (
            <a
              href={`mailto:${businessListing.public_email}`}
              className="font-medium hover:underline"
              style={{ color: 'var(--site-link)' }}
            >
              {businessListing.public_email}
            </a>
          )}
          {businessListing?.public_phone && (
            <>
              {businessListing.public_email && ' or '}
              <a
                href={`tel:${businessListing.public_phone}`}
                className="font-medium hover:underline"
                style={{ color: 'var(--site-link)' }}
              >
                {businessListing.public_phone}
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
